/**
 * Application shell — custom, responsive sidebar layout, no top bar.
 *
 * Sidebar content (top → bottom): brand wordmark (small "strichliste" over a
 * big "365" in Palapa Neue, each scaled UNIFORMLY to fill the sidebar width),
 * the resource-driven nav (Finance group open by default), and a footer with
 * the current user + dark-mode toggle.
 *
 * Responsive:
 *   - Desktop (≥ lg): fixed sticky <Sider>, collapsible to a 64px icon rail.
 *   - Mobile (< lg): a floating hamburger opens an off-canvas <Drawer> holding
 *     the same sidebar content; selecting a nav item closes it.
 *
 * Built on AntD `Layout` + Refine's `useMenu` (instead of `ThemedLayoutV2`)
 * so we control the logo-area height, the default-open group, and the footer.
 * Theme-aware via the AntD `light`/`dark` sider theme + `fill=currentColor`.
 */

import {
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMenu } from "@refinedev/core";
import {
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Grid,
  Layout,
  Menu,
  Space,
  Tooltip,
  Typography,
  type MenuProps,
} from "antd";
import {
  BulbFilled,
  BulbOutlined,
  CloseOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { api } from "../api/client";
import { BRAND_365_GRADIENT, FitLine } from "../components/brand";
import { ChangePasswordModal } from "../components/ChangePasswordModal";
import { NodeLivenessBanner } from "../components/NodeLivenessBanner";
import { UncategorisedBadge } from "../components/UncategorisedBadge";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useDarkMode } from "../theme/useDarkMode";

const { Text } = Typography;
const { Sider, Content } = Layout;

function BrandWordmark({ collapsed }: { collapsed: boolean }) {
  const { mode } = useDarkMode();
  const grad = mode === "dark" ? BRAND_365_GRADIENT.dark : BRAND_365_GRADIENT.light;
  return (
    <Link
      to="/"
      aria-label="strichliste 365 — home"
      style={{ color: "inherit", display: "block" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: collapsed ? 0 : 8,
          padding: collapsed ? "16px 12px 10px" : "20px 20px 14px",
        }}
      >
        {!collapsed && (
          <FitLine
            text="strichliste"
            weight={700}
            fill="var(--ant-color-text, currentColor)"
          />
        )}
        <FitLine text="365" weight={900} gradient={grad} />
      </div>
    </Link>
  );
}

// Inner sidebar content — rendered inside the desktop <Sider> or the mobile
// <Drawer>. The footer (user + dark-toggle) is pinned (menu scrolls between
// the logo and it), so the controls stay visible. `onToggleCollapse` (desktop)
// adds the icon-rail toggle; `onClose` (mobile drawer) adds a Close button and
// closes the drawer when a nav item is picked.
function SidebarContent({
  collapsed,
  onToggleCollapse,
  onClose,
}: {
  collapsed: boolean;
  onToggleCollapse?: () => void;
  onClose?: () => void;
}) {
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const { mode, toggle } = useDarkMode();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const username = user?.username ?? "—";
  const role = user?.is_superadmin ? "superadmin" : (user?.role ?? "viewer");

  const onLogout = async () => {
    try {
      await api.logout();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  type Node = (typeof menuItems)[number];
  const toItems = (nodes: Node[]): NonNullable<MenuProps["items"]> =>
    nodes.map((n) => ({
      key: n.key,
      icon: n.icon,
      label: n.route ? <Link to={n.route}>{n.label}</Link> : n.label,
      children:
        n.children && n.children.length ? toItems(n.children) : undefined,
    }));

  // Finance open by default; the menu is an accordion — opening another
  // top-level group (e.g. Settings) collapses the others, so a long menu
  // never pushes the pinned footer controls out of reach.
  const stockKey = menuItems.find((n) => n.name === "stock")?.key;
  const rootSubmenuKeys = menuItems
    .filter((n) => n.children && n.children.length)
    .map((n) => n.key);
  // Seed the open state from an effect, NOT useState's initialiser:
  // useMenu() returns an empty menuItems on the first render, so the
  // initialiser would lock in [] before the Finance key ("stock") exists.
  // We open Finance by default, plus the current route's group (if it's a
  // different one) so the active item stays visible. Runs once per mount.
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const seededOpen = useRef(false);
  useEffect(() => {
    if (seededOpen.current || menuItems.length === 0) return;
    const routeGroup =
      defaultOpenKeys.length > 0
        ? [defaultOpenKeys[defaultOpenKeys.length - 1]]
        : [];
    setOpenKeys(
      Array.from(new Set([...(stockKey ? [stockKey] : []), ...routeGroup])),
    );
    seededOpen.current = true;
  }, [menuItems, defaultOpenKeys, stockKey]);
  const onOpenChange: MenuProps["onOpenChange"] = (keys) => {
    const next = keys as string[];
    const latest = next.find((k) => !openKeys.includes(k));
    if (latest && rootSubmenuKeys.includes(latest)) {
      setOpenKeys([latest]); // accordion: only the just-opened root stays open
    } else {
      setOpenKeys(next);
    }
  };
  const siderTheme = mode === "dark" ? "dark" : "light";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <BrandWordmark collapsed={collapsed} />
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        <Menu
          mode="inline"
          theme={siderTheme}
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          items={toItems(menuItems)}
          onClick={() => onClose?.()}
          style={{ borderInlineEnd: 0 }}
        />
      </div>
      <div
        style={{
          borderTop: "1px solid var(--ant-color-split, rgba(127,127,127,0.2))",
          padding: collapsed ? "8px 0" : "10px 12px",
          display: "flex",
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Dropdown
          menu={{
            items: [
              ...(user?.is_superadmin
                ? []
                : [
                    {
                      key: "change-password",
                      icon: <KeyOutlined />,
                      label: "Change password",
                      onClick: () => setPwOpen(true),
                    },
                  ]),
              {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "Sign out",
                onClick: () => void onLogout(),
              },
            ],
          }}
        >
          <Space
            size="small"
            style={{
              cursor: "pointer",
              flex: collapsed ? undefined : 1,
              minWidth: 0,
            }}
          >
            <Avatar size="small" icon={<UserOutlined />} />
            {!collapsed && (
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Text strong>{username}</Text>{" "}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({role})
                </Text>
              </span>
            )}
          </Space>
        </Dropdown>
        {!collapsed && <UncategorisedBadge />}
        <Tooltip
          title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <Button
            type="text"
            shape="circle"
            icon={mode === "dark" ? <BulbFilled /> : <BulbOutlined />}
            onClick={toggle}
            aria-label="Toggle dark mode"
          />
        </Tooltip>
        {onToggleCollapse && (
          <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"}>
            <Button
              type="text"
              shape="circle"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onToggleCollapse}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            />
          </Tooltip>
        )}
        {/* No Tooltip here: this button unmounts the whole drawer on tap,
            which would orphan a hover-tooltip popup on touch devices. */}
        {onClose && (
          <Button
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label="Close menu"
          />
        )}
      </div>
      <ChangePasswordModal
        open={pwOpen}
        hasPassword={!!user?.has_password}
        onClose={() => setPwOpen(false)}
      />
    </div>
  );
}

export const AppShell = ({ children }: PropsWithChildren) => {
  const screens = Grid.useBreakpoint();
  const { mode } = useDarkMode();
  // Undefined on first paint → treat as desktop to avoid a hamburger flash.
  const isMobile = screens.lg === false;
  const [collapsed, setCollapsed] = useState(false); // desktop icon rail
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile drawer
  const siderTheme = mode === "dark" ? "dark" : "light";

  return (
    // 100dvh (not 100vh): on mobile, 100vh includes the area behind the
    // browser URL bar, making the shell taller than the visible viewport so
    // the pinned drawer footer falls below the fold.
    <Layout style={{ minHeight: "100dvh" }}>
      <NodeLivenessBanner />
      {isMobile ? (
        <>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            style={{
              position: "fixed",
              top: 8,
              insetInlineStart: 8,
              zIndex: 999,
              background: "var(--ant-color-bg-container)",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.25)",
            }}
          />
          <Drawer
            placement="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={264}
            styles={{
              // Pin the drawer panel to the visible viewport (not 100vh,
              // which spills behind the mobile URL bar → footer off-screen).
              wrapper: { height: "100dvh" },
              content: { height: "100dvh" },
              body: {
                padding: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
              header: { display: "none" },
            }}
          >
            <SidebarContent collapsed={false} onClose={() => setDrawerOpen(false)} />
          </Drawer>
        </>
      ) : (
        <Sider
          width={224}
          collapsedWidth={64}
          collapsible
          collapsed={collapsed}
          trigger={null}
          theme={siderTheme}
          style={{ height: "100dvh", position: "sticky", top: 0, insetInlineStart: 0 }}
        >
          <SidebarContent
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((c) => !c)}
          />
        </Sider>
      )}
      <Layout style={{ minWidth: 0 }}>
        <Content
          style={{
            minWidth: 0,
            // FRAMELESS: content runs edge-to-edge — no gray --ant-color-bg-
            // layout inset around the page. Keep 52px top ONLY on mobile so
            // the fixed hamburger doesn't overlap content; 0 elsewhere.
            // NOTE: CatalogWorkspace's height calc is COUPLED to these two
            // padding values (paddingTop + paddingBottom). To switch to a
            // padded look, change these AND that calc together (commit doc
            // lists the option values).
            paddingInline: 0,
            paddingBottom: 0,
            paddingTop: isMobile ? 52 : 0,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};
